import { useGlobalContext } from "context/GlobalContext";
import { createContext, useContext, useEffect, useState } from "react";
import { message } from "constants/responses/message";
import { SECRETS } from "configs/secrets";
import { taskCompletionStatusOldApi } from "API/apis/dashboard/taskCompletionStatusOld";

const initialValue = {
  records: [],
  recordsCount: 0,
};

const TaskDashboardContext = createContext({
  assignedToMePendingRecordCount: 0,
  setAssignedToMePendingRecordCount: () => {},
  assignedByMePendingRecordCount: 0,
  setAssignedByMePendingRecordCount: () => {},
  assignedToMeAcceptedRecordCount: 0,
  setAssignedToMeAcceptedRecordCount: () => {},
  assignedByMeAcceptedRecordCount: 0,
  setAssignedByMeAcceptedRecordCount: () => {},

  assignedToMePendingRecord: [],
  setAssignedToMePendingRecord: () => {},
  assignedByMePendingRecord: [],
  setAssignedByMePendingRecord: () => {},
  assignedToMeAcceptedRecord: [],
  setAssignedToMeAcceptedRecord: () => {},
  assignedByMeAcceptedRecord: [],
  setAssignedByMeAcceptedRecord: () => {},

  reloadRequired: false,
  setReloadRequired: () => {},
});

export const TaskDashboardProvider = ({ children }) => {
  const [assignedToMePendingRecordCount, setAssignedToMePendingRecordCount] =
    useState(initialValue.recordsCount);
  const [assignedByMePendingRecordCount, setAssignedByMePendingRecordCount] =
    useState(initialValue.recordsCount);
  const [assignedToMeAcceptedRecordCount, setAssignedToMeAcceptedRecordCount] =
    useState(initialValue.recordsCount);
  const [assignedByMeAcceptedRecordCount, setAssignedByMeAcceptedRecordCount] =
    useState(initialValue.recordsCount);

  const [assignedToMePendingRecord, setAssignedToMePendingRecord] = useState(
    initialValue.records
  );
  const [assignedByMePendingRecord, setAssignedByMePendingRecord] = useState(
    initialValue.records
  );
  const [assignedToMeAcceptedRecord, setAssignedToMeAcceptedRecord] = useState(
    initialValue.records
  );
  const [assignedByMeAcceptedRecord, setAssignedByMeAcceptedRecord] = useState(
    initialValue.records
  );

  const [reloadRequired, setReloadRequired] = useState(false);
  const { setIsInitializing, setError, setIsLoading } = useGlobalContext();

  useEffect(() => {
    setIsInitializing(true);
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { data, status } = await taskCompletionStatusOldApi();
        if (status !== 200) {
          throw new Error(message.error.generalFailure);
        }

        setAssignedToMePendingRecordCount(
          data.assignedToMePending.recordsCount
        );
        setAssignedToMePendingRecord(data.assignedToMePending.records);

        setAssignedToMeAcceptedRecordCount(
          data.assignedToMeAccepted.recordsCount
        );
        setAssignedToMeAcceptedRecord(data.assignedToMeAccepted.records);

        setAssignedByMePendingRecordCount(
          data.assignedByMePending.recordsCount
        );
        setAssignedByMePendingRecord(data.assignedByMePending.records);

        setAssignedByMeAcceptedRecordCount(
          data.assignedByMeAccepted.recordsCount
        );
        setAssignedByMeAcceptedRecord(data.assignedByMeAccepted.records);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
        setIsInitializing(false);
        setReloadRequired(false);
      }
    };
    fetchData();
    const intervalId = setInterval(() => {
      fetchData();
    }, Number(SECRETS.TASK_DASHBOARD_REFRESH_TIME));
    return () => clearInterval(intervalId);
  }, [reloadRequired]);

  return (
    <TaskDashboardContext.Provider
      value={{
        assignedToMePendingRecordCount,
        setAssignedToMePendingRecordCount,
        assignedByMePendingRecordCount,
        setAssignedByMePendingRecordCount,
        assignedToMeAcceptedRecordCount,
        setAssignedToMeAcceptedRecordCount,
        assignedByMeAcceptedRecordCount,
        setAssignedByMeAcceptedRecordCount,
        assignedToMePendingRecord,
        setAssignedToMePendingRecord,
        assignedByMePendingRecord,
        setAssignedByMePendingRecord,
        assignedToMeAcceptedRecord,
        setAssignedToMeAcceptedRecord,
        assignedByMeAcceptedRecord,
        setAssignedByMeAcceptedRecord,
        reloadRequired,
        setReloadRequired,
      }}
    >
      <>{children}</>
    </TaskDashboardContext.Provider>
  );
};

export const useTaskDashboardContext = () => {
  const context = useContext(TaskDashboardContext);
  if (!context) {
    throw new Error(
      "useTaskDashboardContext must be used within a TaskDashboardProvider"
    );
  }
  return context;
};
